import React from "react";
import { ArrowUpIcon, ArrowDownIcon } from "lucide-react";
import { useTableData } from "../../../../hooks/useTableData";
import Image from "../../../Tags/Image";
import Pagination from "../../../Pagination";
import TableLoader from "../../../Loaders/TableLoader";
import NoData from "../../../NoData";
import { useTheme } from "../../../ThemeProvider";

export default function RepStatsTable({
  className = "",
  fetchFn,
  queryKey = "analytics_rep_stats_table",
  defaultSort = "",
  enabled = true,
  filters,
  onDataLoaded,
  restObject = {},
}) {
  const { theme } = useTheme();
  
  const {
    data,
    sort,
    handleSetSort,
    isLoading,
    isSuccess,
    isError,
    columnMetadata,
    paginationInfo,
    getPageNumber,
  } = useTableData({
    queryKey,
    fetchFn,
    defaultSort,
    enabled,
    defaultFilters: filters,
    restObject,
    onSuccess: (data) => {
        if (onDataLoaded) {
            onDataLoaded(data);
        }
    }
  });

  const SortIcon = ({ column }) => {
    if (sort === "" || (sort !== column && sort !== `-${column}`)) {
      return <Image src="/images/icons/dual-arrow.svg" width="7" height="7" className="inline-block ml-1" />;
    }
    return sort === column ? (
      <ArrowDownIcon width={12} className="inline-block ml-1" />
    ) : (
      <ArrowUpIcon width={12} className="inline-block ml-1" />
    );
  };

  if (isLoading && !isSuccess) {
    return <TableLoader />;
  }

  if (isSuccess && (!data || data.length === 0)) {
    return (
      <NoData
        heading="No rep statistics found"
        description="No data available for the selected criteria"
      />
    );
  }

  // Helper to safely access column metadata
  const getColMeta = (key) => columnMetadata?.[key] || {};

  return (
    <div
      className={`bg-white dark:bg-[#181D27] border border-gray-200 dark:border-[#22262F] rounded-lg overflow-hidden ${className}`}
    >
      <div className="p-4 border-b border-gray-200 dark:border-[#22262F] flex justify-between items-center">
         <h3 className="font-semibold text-gray-900 dark:text-white">Rep statistics</h3>
         <div className="flex gap-2">
            {/* Search and Download buttons would go here if needed, keeping it simple for now as per requirements */}
         </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-b-[#E9EAEB] dark:border-b-[#22262F]">
            <tr>
              {/* Rep Name */}
              {getColMeta("rep_name").visible && (
                <th className="px-6 py-3 text-left text-xs font-medium text-[#717680] uppercase tracking-wider">
                    {getColMeta("rep_name").sortable ? (
                    <button
                        className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
                        onClick={() => handleSetSort("rep_name")}
                    >
                        {getColMeta("rep_name").display_name || "Rep name"}
                        <SortIcon column="rep_name" />
                    </button>
                    ) : (
                    <span>{getColMeta("rep_name").display_name || "Rep name"}</span>
                    )}
                </th>
              )}

              {/* Call Volume */}
              {getColMeta("call_volume").visible && (
                <th className="px-6 py-3 text-left text-xs font-medium text-[#717680] uppercase tracking-wider">
                    {getColMeta("call_volume").sortable ? (
                    <button
                        className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
                        onClick={() => handleSetSort("call_volume")}
                    >
                        {getColMeta("call_volume").display_name || "Call volume"}
                        <SortIcon column="call_volume" />
                    </button>
                    ) : (
                    <span>{getColMeta("call_volume").display_name || "Call volume"}</span>
                    )}
                </th>
              )}

              {/* Avg Call Duration */}
              {getColMeta("avg_call_duration_minutes").visible && (
                <th className="px-6 py-3 text-left text-xs font-medium text-[#717680] uppercase tracking-wider">
                    {getColMeta("avg_call_duration_minutes").sortable ? (
                    <button
                        className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
                        onClick={() => handleSetSort("avg_call_duration_minutes")}
                    >
                        {getColMeta("avg_call_duration_minutes").display_name || "Call duration (Avg)"}
                        <SortIcon column="avg_call_duration_minutes" />
                    </button>
                    ) : (
                    <span>{getColMeta("avg_call_duration_minutes").display_name || "Call duration (Avg)"}</span>
                    )}
                </th>
              )}

              {/* Total Talk Time */}
              {getColMeta("total_talk_time_minutes").visible && (
                <th className="px-6 py-3 text-left text-xs font-medium text-[#717680] uppercase tracking-wider">
                    {getColMeta("total_talk_time_minutes").sortable ? (
                    <button
                        className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
                        onClick={() => handleSetSort("total_talk_time_minutes")}
                    >
                        {getColMeta("total_talk_time_minutes").display_name || "Total talk time"}
                        <SortIcon column="total_talk_time_minutes" />
                    </button>
                    ) : (
                    <span>{getColMeta("total_talk_time_minutes").display_name || "Total talk time"}</span>
                    )}
                </th>
              )}

              {/* Connect Rate */}
              {getColMeta("connect_rate").visible && (
                <th className="px-6 py-3 text-left text-xs font-medium text-[#717680] uppercase tracking-wider">
                    {getColMeta("connect_rate").sortable ? (
                    <button
                        className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
                        onClick={() => handleSetSort("connect_rate")}
                    >
                        {getColMeta("connect_rate").display_name || "Connect rate"}
                        <SortIcon column="connect_rate" />
                    </button>
                    ) : (
                    <span>{getColMeta("connect_rate").display_name || "Connect rate"}</span>
                    )}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-[#0C0E12] divide-y divide-gray-200 dark:divide-[#22262F]">
            {data.map((row, index) => {
              return (
                <tr key={`${row.user_id}-${index}`} className="hover:bg-gray-50 dark:hover:bg-[#13161C] transition-colors">
                  {/* Rep Name */}
                  {getColMeta("rep_name").visible && (
                    <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium mr-3">
                             {/* Placeholder avatar or initials */}
                             {row.rep_name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase()}
                        </div>
                        <div className="font-semibold text-[#101828] dark:text-white text-sm">
                            {row.rep_name}
                        </div>
                        </div>
                    </td>
                  )}
                  
                  {/* Call Volume */}
                  {getColMeta("call_volume").visible && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#535862] dark:text-[#94979C]">
                        {row.call_volume}
                    </td>
                  )}

                  {/* Avg Call Duration */}
                  {getColMeta("avg_call_duration_minutes").visible && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#535862] dark:text-[#94979C]">
                        {row.avg_call_duration_minutes} min
                    </td>
                  )}

                  {/* Total Talk Time */}
                  {getColMeta("total_talk_time_minutes").visible && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#535862] dark:text-[#94979C]">
                        {row.total_talk_time_minutes} min
                    </td>
                  )}

                  {/* Connect Rate */}
                  {getColMeta("connect_rate").visible && (
                    <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{row.connect_rate}%</span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {paginationInfo && paginationInfo.total > 0 && (
        <div className="flex items-center justify-between rounded-[--radius] py-3 px-6">
          <div className="flex-1">
            <Pagination data={paginationInfo} getPageNumber={getPageNumber} />
          </div>
        </div>
      )}
    </div>
  );
}
